import { supabase } from './supabase';

/**
 * The customer's Sentinel records over the chat (Gary, 18 Oct): Customer, Booking and History. Reads go
 * straight to the tables, as the Customer card did; booking changes go through Sentinel's own booking
 * functions (booking-manage, booking-assign), so the rules, operator checks and customer messages are the
 * ones Sentinel's booking screens use. The mock-up in the local preview uses SAMPLE_RECORDS instead.
 */

export interface RecCustomer {
  id: string;
  type: 'individual' | 'business';
  firstName: string;
  lastName: string;
  company: string;
  mobile: string;
  phone: string;
  email: string;
  street: string;
  suburb: string;
  city: string;
  postcode: string;
  notes: string;
  tier: string | null;
  since: string | null;
  total: number;
  last12: number;
  territoryId: string | null;
  /** The saved address as stored, so a save keeps its other parts (state, country, attention). */
  billing: Record<string, unknown>;
}

export interface RecOperator { id: string; name: string; available?: boolean; reason?: string }

export type BookingStatus = 'pending' | 'confirmed' | 'completed' | 'cancelled';

export interface RecBooking {
  id: string;
  code: string;
  date: string;
  time: string;
  end: string | null;
  beds: number;
  status: BookingStatus;
  street: string;
  suburb: string;
  city: string;
  note: string;
  territoryId: string;
  area: string;
  operators: RecOperator[];
}

export interface RecService {
  key: string;
  date: string;
  items: string;
  amount: number | null;
  operator: string | null;
  invoice: string | null;
  paid: boolean;
  payUrl: string | null;
}

export interface RecArea { id: string; name: string; shortCode: string }

export interface NewBooking {
  customer: RecCustomer;
  territoryId: string;
  date: string;
  time: string;
  beds: number;
  street: string;
  suburb: string;
  city: string;
  note: string;
  operatorIds: string[];
  tell: boolean;
}

/** Everything the three panels need, live or sample. */
export interface RecordsApi {
  customer(): Promise<RecCustomer | null>;
  saveCustomer(c: RecCustomer): Promise<void>;
  nextBooking(): Promise<RecBooking | null>;
  areas(): Promise<RecArea[]>;
  slots(territoryId: string, date: string): Promise<string[]>;
  operators(territoryId: string, date: string, time: string): Promise<RecOperator[]>;
  create(b: NewBooking): Promise<string>;
  update(b: RecBooking, patch: { date?: string; time?: string; beds?: number; street?: string; suburb?: string; city?: string; note?: string }, tell: boolean): Promise<void>;
  cancel(b: RecBooking, reason: string, notes: string, tell: boolean): Promise<void>;
  assign(b: RecBooking, operatorId: string, tell: boolean): Promise<void>;
  history(): Promise<RecService[]>;
}

/** What this person may do, as Sentinel's buttons allow it (security review, 18 Oct; booking-manage and
 *  booking-assign hold the same line): Customers contribute to edit a customer, Bookings contribute to book,
 *  edit or move, Bookings manage to cancel or assign. Head office may do all of it. */
export interface RecordRights { editCustomer: boolean; book: boolean; cancel: boolean; assign: boolean }

export function rightsFor(headOffice: boolean, perms: Record<string, { level?: string } | undefined> | null): RecordRights {
  if (headOffice) return { editCustomer: true, book: true, cancel: true, assign: true };
  const at = (key: string, level: 'contribute' | 'manage') => {
    const have = perms?.[key]?.level;
    return have === 'manage' || (level === 'contribute' && have === 'contribute');
  };
  return { editCustomer: at('customers', 'contribute'), book: at('bookings', 'contribute'), cancel: at('bookings', 'manage'), assign: at('bookings', 'manage') };
}

/** Sentinel's job length: 15 minutes to set up, 15 a bed, 15 to pack up (wizardConstants). */
export const jobMinutes = (beds: number) => 15 + 15 * beds + 15;

export function endTime(time: string, beds: number): string {
  const [h, m] = time.split(':').map(Number);
  const t = (h ?? 0) * 60 + (m ?? 0) + jobMinutes(beds);
  return `${String(Math.floor(t / 60) % 24).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
}

/** Beds on a booking: the wizard stores {bed_count}, the website and edit form [{quantity}]. */
function bedsOf(services: unknown, name: string | null): number {
  if (services && typeof services === 'object' && !Array.isArray(services)) {
    const n = Number((services as { bed_count?: unknown }).bed_count);
    if (n > 0) return n;
  }
  if (Array.isArray(services)) {
    const n = (services as { quantity?: unknown }[]).reduce((t, s) => t + (Number(s.quantity) || 0), 0);
    if (n > 0) return n;
  }
  const m = /(\d+)\s*bed/i.exec(name ?? '');
  return m ? Number(m[1]) : 1;
}

const str = (v: unknown) => (typeof v === 'string' ? v : '');

/** The function's own error text, not supabase-js's "non-2xx status" wrapper. */
async function call(fn: string, body: Record<string, unknown>): Promise<Record<string, unknown>> {
  const { data, error } = await supabase.functions.invoke(fn, { body });
  if (error) {
    let message = error.message;
    try {
      const ctx = (error as { context?: Response }).context;
      const j = ctx ? await ctx.json() as { error?: string } : null;
      if (j?.error) message = j.error;
    } catch { /* keep the default message */ }
    throw new Error(message);
  }
  if (data && typeof data === 'object' && 'error' in data && data.error) throw new Error(String(data.error));
  return (data ?? {}) as Record<string, unknown>;
}

/**
 * A chat with a lead but no customer yet (Gary, 19 Oct: Laura's chat had no Booking button). The lead's details stand
 * in for the customer; booking-manage creates the customer from them and marks the lead booked (lead_id).
 */
async function leadAsCustomer(leadId: string): Promise<RecCustomer | null> {
  const { data: l } = await supabase.from('leads')
    .select('id, first_name, last_name, email, phone, address, city, territory_id').eq('id', leadId).maybeSingle();
  if (!l) return null;
  return {
    id: '', type: 'individual', firstName: str(l.first_name), lastName: str(l.last_name), company: '',
    mobile: str(l.phone), phone: '', email: str(l.email), street: str(l.address), suburb: '', city: str(l.city) === 'TBC' ? '' : str(l.city),
    postcode: '', notes: '', tier: null, since: null, total: 0, last12: 0,
    territoryId: (l.territory_id as string | null) ?? null, billing: {},
  };
}

export function liveRecords(chatId: string, customerId: string, leadId: string | null = null): RecordsApi {
  return {
    async customer() {
      if (!customerId) return leadId ? leadAsCustomer(leadId) : null;
      const { data: c } = await supabase.from('customers')
        .select('id, customer_type, first_name, last_name, customer_name, company_name, email, phone, mobile, billing_address, notes, loyalty_tier, services_last_12m, total_services, created_at, territory_id')
        .eq('id', customerId).maybeSingle();
      if (!c) return null;
      const addr = (c.billing_address && typeof c.billing_address === 'object' ? c.billing_address : {}) as Record<string, unknown>;
      const [first = '', ...rest] = str(c.customer_name).split(' ');
      return {
        id: c.id as string,
        type: c.customer_type === 'business' ? 'business' : 'individual',
        firstName: str(c.first_name) || (c.customer_type === 'business' ? '' : first),
        lastName: str(c.last_name) || (c.customer_type === 'business' ? '' : rest.join(' ')),
        company: str(c.company_name),
        mobile: str(c.mobile),
        phone: str(c.phone),
        email: str(c.email),
        street: str(addr.address),
        suburb: str(addr.suburb),
        city: str(addr.city),
        postcode: str(addr.zip),
        notes: str(c.notes),
        tier: (c.loyalty_tier as string | null) ?? null,
        since: (c.created_at as string | null) ?? null,
        total: Number(c.total_services ?? 0),
        last12: Number(c.services_last_12m ?? 0),
        territoryId: (c.territory_id as string | null) ?? null,
        billing: addr,
      };
    },

    // The same fields and name rule as Sentinel's customer screen (CustomerDetailModal handleSave).
    async saveCustomer(c) {
      const name = c.type === 'business' && c.company.trim()
        ? c.company.trim()
        : [c.firstName.trim(), c.lastName.trim()].filter(Boolean).join(' ') || c.email.trim();
      const { error } = await supabase.from('customers').update({
        customer_name: name,
        first_name: c.firstName.trim() || null,
        last_name: c.lastName.trim() || null,
        company_name: c.company.trim() || null,
        email: c.email.trim() || null,
        mobile: c.mobile.trim() || null,
        phone: c.phone.trim() || null,
        billing_address: { ...c.billing, address: c.street.trim(), suburb: c.suburb.trim(), city: c.city.trim(), zip: c.postcode.trim() },
        notes: c.notes.trim() || null,
      }).eq('id', c.id);
      if (error) throw new Error(/duplicate|unique/i.test(error.message) ? 'Another customer already has this email.' : error.message);
    },

    async nextBooking() {
      if (!customerId) return null;
      const today = new Date().toLocaleDateString('en-CA');
      const { data: chat } = await supabase.from('conversations').select('source').eq('id', chatId).maybeSingle();
      const chatBooking = str((chat?.source as Record<string, unknown> | null)?.booking_id) || null;
      const fields = 'id, booking_code, service_date, service_time, service_end_time, services, service_name, status, service_address, suburb, city, notes, territory_id, territories(name)';
      const { data: rows } = await supabase.from('bookings').select(fields)
        .eq('customer_id', customerId).neq('status', 'cancelled').gte('service_date', today)
        .order('service_date').order('service_time').limit(5);
      const list = (rows ?? []) as Record<string, unknown>[];
      let b = list.find((r) => r.id === chatBooking) ?? list[0];
      // Nothing ahead: the latest cancelled booking, so a cancel shows as done (Gary, 19 Oct: Lauren's
      // cancelled booking was not there and the button only offered New booking).
      if (!b) {
        const { data: gone } = await supabase.from('bookings').select(fields)
          .eq('customer_id', customerId).eq('status', 'cancelled')
          .order('service_date', { ascending: false }).order('service_time', { ascending: false }).limit(5);
        const cancelled = (gone ?? []) as Record<string, unknown>[];
        b = cancelled.find((r) => r.id === chatBooking) ?? cancelled[0];
      }
      if (!b) return null;
      const { data: assigned } = await supabase.from('booking_assignments')
        .select('user_id, status, users!booking_assignments_user_id_fkey(first_name, last_name)').eq('booking_id', b.id as string).neq('status', 'cancelled');
      const operators = ((assigned ?? []) as unknown as { user_id: string; users: { first_name: string | null; last_name: string | null } | null }[])
        .map((a) => ({ id: a.user_id, name: [a.users?.first_name, a.users?.last_name].filter(Boolean).join(' ') || 'Operator' }));
      return {
        id: b.id as string,
        code: str(b.booking_code),
        date: str(b.service_date).slice(0, 10),
        time: str(b.service_time).slice(0, 5),
        end: str(b.service_end_time).slice(0, 5) || null,
        beds: bedsOf(b.services, b.service_name as string | null),
        status: (b.status as BookingStatus) ?? 'pending',
        street: str(b.service_address),
        suburb: str(b.suburb),
        city: str(b.city),
        note: str(b.notes),
        territoryId: b.territory_id as string,
        area: str((b.territories as { name?: string } | null)?.name),
        operators,
      };
    },

    async areas() {
      // The areas Sentinel's booking wizard offers: active, with booking on (no head office or purchase entries).
      const { data } = await supabase.from('territories').select('id, name, short_code').eq('status', 'active').eq('consumer_booking_enabled', true).order('name');
      return ((data ?? []) as { id: string; name: string; short_code: string }[]).map((t) => ({ id: t.id, name: t.name, shortCode: t.short_code }));
    },

    // Free times for the area, as Sentinel's booking wizard asks for them (staff skip lead time and closed days).
    async slots(territoryId, date) {
      const { data: t } = await supabase.from('territories').select('short_code').eq('id', territoryId).maybeSingle();
      if (!t?.short_code) return [];
      const { data: s } = await supabase.auth.getSession();
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/booking-public-availability?operation=slots&territory=${encodeURIComponent(t.short_code as string)}&date=${date}&live_testing_only=1&skip_lead_time=true&skip_closed_days=true`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${s.session?.access_token ?? ''}` } });
      const j = await res.json() as { slots?: string[]; error?: string };
      if (j.error) throw new Error(j.error);
      return j.slots ?? [];
    },

    async operators(territoryId, date, time) {
      const j = await call('booking-assign', { operation: 'list_operators', territory_id: territoryId, date, time });
      return ((j.operators ?? []) as RecOperator[]).map((o) => ({ id: o.id, name: o.name, available: o.available, reason: o.reason }));
    },

    // As Sentinel's booking wizard sends it (BookingWizardModal handleCreate).
    async create(n) {
      const end = endTime(n.time, n.beds);
      const j = await call('booking-manage', {
        operation: 'create',
        territory_id: n.territoryId,
        customer_id: n.customer.id || undefined,
        ...(n.customer.id ? {} : { lead_id: leadId ?? undefined }),
        customer_name: [n.customer.firstName, n.customer.lastName].filter(Boolean).join(' ') || n.customer.company,
        customer_email: n.customer.email,
        customer_phone: n.customer.mobile || n.customer.phone,
        customer_address: n.street,
        service_address: n.street,
        suburb: n.suburb || null,
        city: n.city || null,
        service_date: `${n.date}T${n.time}:00`,
        service_end_time: `${n.date}T${end}:00`,
        duration_minutes: jobMinutes(n.beds),
        services: { bed_count: n.beds, extras: [], source: 'portal' },
        service_name: `${n.beds} bed${n.beds > 1 ? 's' : ''}`,
        status: n.operatorIds.length ? 'confirmed' : 'pending',
        operator_ids: n.operatorIds,
        booking_source: 'portal',
        source: 'portal',
        notes: n.note.trim() || null,
        skip_notification: !n.tell,
      });
      return str((j.booking as Record<string, unknown> | undefined)?.booking_code);
    },

    // Only what changed, so nothing else on the booking is touched (the code, the source, the status).
    async update(b, p, tell) {
      const beds = p.beds ?? b.beds;
      const time = p.time ?? b.time;
      const body: Record<string, unknown> = { operation: 'update', id: b.id, skip_notification: !tell };
      if (p.date !== undefined) body.service_date = p.date;
      if (p.time !== undefined || p.beds !== undefined) {
        body.start_time = time;
        body.end_time = endTime(time, beds);
        body.duration_minutes = jobMinutes(beds);
      }
      if (p.beds !== undefined) {
        body.services = [{ type: 'bed_cleaning', name: 'Bed Cleaning', quantity: beds }];
        body.service_name = `${beds} bed${beds > 1 ? 's' : ''}`;
      }
      if (p.street !== undefined) body.service_address = p.street;
      if (p.suburb !== undefined) body.suburb = p.suburb;
      if (p.city !== undefined) body.city = p.city;
      if (p.note !== undefined) body.notes = p.note;
      await call('booking-manage', body);
    },

    async cancel(b, reason, notes, tell) {
      await call('booking-manage', { operation: 'cancel', id: b.id, cancellation_reason: reason, cancellation_notes: notes.trim() || null, skip_notification: !tell });
    },

    // One operator, as the chat chooses it: the new one on, the old ones off.
    async assign(b, operatorId, tell) {
      await call('booking-assign', { operation: 'assign', booking_id: b.id, operator_ids: [operatorId], skip_notification: !tell });
      for (const o of b.operators.filter((x) => x.id !== operatorId)) {
        await call('booking-assign', { operation: 'unassign', booking_id: b.id, operator_id: o.id });
      }
    },

    async history() {
      if (!customerId) return [];
      const { data } = await supabase.from('unified_service_reports')
        .select('id, source, service_date, items, total_beds, total_amount, operator_name, invoice_number, status, paid_at, payment_url')
        .eq('customer_id', customerId).order('service_date', { ascending: false }).limit(30);
      const rows = (data ?? []) as Record<string, unknown>[];
      const numbers = rows.map((r) => str(r.invoice_number)).filter(Boolean);
      const balances = new Map<string, number>();
      if (numbers.length) {
        const { data: inv } = await supabase.from('invoices').select('invoice_number, balance_due, status, payment_url').in('invoice_number', numbers);
        for (const i of (inv ?? []) as { invoice_number: string; balance_due: number | string | null; status: string }[]) {
          balances.set(i.invoice_number, i.status === 'paid' || i.status === 'void' ? 0 : Number(i.balance_due ?? 0));
        }
      }
      return rows.map((r) => {
        const invoice = str(r.invoice_number) || null;
        const owed = invoice ? balances.get(invoice) : undefined;
        return {
          key: `${str(r.source)}-${str(r.id)}`,
          date: str(r.service_date).slice(0, 10),
          items: itemsText(r.items, r.total_beds as number | null),
          amount: r.total_amount === null || r.total_amount === undefined ? null : Number(r.total_amount),
          operator: (r.operator_name as string | null) ?? null,
          invoice,
          paid: owed !== undefined ? owed <= 0 : !!r.paid_at || str(r.status).toLowerCase() === 'paid',
          payUrl: (r.payment_url as string | null) ?? null,
        };
      });
    },
  };
}

function itemsText(raw: unknown, beds: number | null): string {
  if (Array.isArray(raw)) {
    const names = (raw as { name?: string; item?: string; item_name?: string; quantity?: number }[])
      .map((i) => `${i.quantity && i.quantity > 1 ? `${i.quantity} × ` : ''}${i.name ?? i.item ?? i.item_name ?? ''}`.trim()).filter(Boolean);
    if (names.length) return names.join(', ');
  }
  return beds ? `${beds} bed${beds > 1 ? 's' : ''}` : 'Service';
}
